import React, { Fragment } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  BoltIcon,
  PencilSquareIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { utcTimeAgo } from "../../../utils/utcTime";

// "Accept Questionnaire" split button as shown in the prototype: a primary indigo button
// with a chevron-down opening a 3-option menu (Autorelease / Accept + Update / Cancel).
// The primary click action defaults to opening the dropdown (matches the screenshot).
const AcceptQuestionnaireSplitButton = ({
  autoreleaseDisabled,
  autoreleaseDisabledReason,
  selectedPast,
  onAutorelease,
  onAcceptAndUpdate,
  onCancelRequest,
}) => {
  const ago = utcTimeAgo(selectedPast?.release_date);

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        type="button"
        className="inline-flex items-center gap-2.5 rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition"
      >
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-white/20">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <span>Accept Questionnaire</span>
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </MenuButton>

      <MenuItems className="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none divide-y divide-gray-100">
        <div className="py-1">
          <MenuItem disabled={autoreleaseDisabled}>
            {({ focus, disabled }) => (
              <button
                type="button"
                onClick={onAutorelease}
                disabled={disabled}
                title={disabled ? autoreleaseDisabledReason : undefined}
                className={`w-full text-left px-4 py-3 flex gap-3 items-start ${
                  disabled
                    ? "opacity-50 cursor-not-allowed"
                    : focus
                    ? "bg-indigo-50"
                    : ""
                }`}
              >
                <BoltIcon
                  className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    Autorelease
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {disabled && autoreleaseDisabledReason
                      ? autoreleaseDisabledReason
                      : `Auto-populate from your last approved and submitted responses ${ago}.`}
                  </span>
                </span>
              </button>
            )}
          </MenuItem>

          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                onClick={onAcceptAndUpdate}
                className={`w-full text-left px-4 py-3 flex gap-3 items-start ${
                  focus ? "bg-indigo-50" : ""
                }`}
              >
                <PencilSquareIcon
                  className="h-5 w-5 text-gray-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    Accept Questionnaire + Update Responses
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Open the questionnaire and edit before submitting.
                  </span>
                </span>
              </button>
            )}
          </MenuItem>
        </div>

        <div className="py-1">
          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                onClick={onCancelRequest}
                className={`w-full text-left px-4 py-3 flex gap-3 items-center ${
                  focus ? "bg-red-50" : ""
                }`}
              >
                <XCircleIcon
                  className="h-5 w-5 text-red-500 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-red-600">
                  Cancel Request
                </span>
              </button>
            )}
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
};

export default AcceptQuestionnaireSplitButton;
